// Updated CRMApiClient.ts - Add includeDetails parameter support
import SkTechApiClient from "./base/SkTechApiClient";

export interface Address {
  id: number;
  locationName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  isPrimaryAddress?: boolean;
  externalCompanyId: number;
  companyId: number;
}

export interface AddressDto {
  locationName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  isPrimaryAddress?: boolean;
}

export interface ExternalCompany {
  id: number;
  name: string;
  edrpou?: string;
  description?: string;
  companyId: number;
  addresses?: Address[];
  events?: CrmEvent[];
  contacts?: Contact[];
}

export interface Contact {
  id: number;
  name: string;
  surname: string;
  phoneNumber?: string;
  email?: string;
  roleId?: number;
  externalCompanyId?: number;
  companyId: number;
  role?: ClientRole;
  externalCompany?: ExternalCompany;
  additionalInfos?: ContactAdditionalInfo[];
  events?: CrmEvent[];
}

export interface ContactAdditionalInfo {
  id: number;
  contactId: number;
  type: string;
  name: string;
}

export interface ClientRole { id: number; name: string; companyId: number; }
export interface ContactType { id: number; name: string; companyId: number; }
export interface CrmEventType { id: number; name: string; companyId: number; }

export interface ContactAdditionalInfoDto {
  type: string;
  name: string;
}

export interface ContactDto {
  name: string;
  surname: string;
  phoneNumber?: string;
  email?: string;
  roleId?: number;
  externalCompanyId?: number;
  additionalInfos?: ContactAdditionalInfoDto[];
}

export interface ClientRoleDto { name: string; }
export interface ContactTypeDto { name: string; }
export interface CrmEventTypeDto { name: string; }

export interface ExternalCompanyDto {
  name: string;
  edrpou?: string;
  description?: string;
  addresses?: AddressDto[];
}

export interface CrmEvent {
  id: number;
  dateStart: string;
  dateEnd: string;
  typeId: number;
  contacts?: Contact[];
  externalCompanies?: ExternalCompany[];
  name: string;
  description?: string;
  companyId: number;
  type?: CrmEventType;
}

export interface CrmEventDto {
  dateStart: string;
  dateEnd: string;
  typeId: number;
  contactIds?: number[];
  externalCompanyIds?: number[];
  name: string;
  description?: string;
}

export default class CRMApiClient extends SkTechApiClient {
  // UPDATED: Contacts with performance optimization
  getContacts = async (includeDetails = false): Promise<Contact[]> => {
    const url = includeDetails ? "/api/Contact?includeDetails=true" : "/api/Contact";
    return this.Get(url).then(r => (r?.status === 200 ? r.data : []));
  };

  getContactById = async (id: number): Promise<Contact | null> =>
    this.Get(`/api/Contact/${id}`).then(r => (r?.status === 200 ? r.data : null));

  createContact = async (dto: ContactDto): Promise<Contact | null> =>
    this.Post("/api/Contact", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null));

  updateContact = async (id: number, dto: ContactDto): Promise<boolean> =>
    this.Put(`/api/Contact/${id}`, dto).then(r => r?.status === 200 || r?.status === 204);

  deleteContact = async (id: number): Promise<boolean> =>
    this.Delete(`/api/Contact/${id}`).then(r => r?.status === 200 || r?.status === 204);

  getClientRoles = async (): Promise<ClientRole[]> =>
    this.Get("/api/ClientRole").then(r => (r?.status === 200 ? r.data : []));

  getClientRoleById = async (id: number): Promise<ClientRole | null> =>
    this.Get(`/api/ClientRole/${id}`).then(r => (r?.status === 200 ? r.data : null));

  createClientRole = async (dto: ClientRoleDto): Promise<ClientRole | null> =>
    this.Post("/api/ClientRole", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null));

  updateClientRole = async (id: number, dto: ClientRoleDto): Promise<boolean> =>
    this.Put(`/api/ClientRole/${id}`, dto).then(r => r?.status === 200 || r?.status === 204);

  deleteClientRole = async (id: number): Promise<boolean> =>
    this.Delete(`/api/ClientRole/${id}`).then(r => r?.status === 200 || r?.status === 204);

  getContactTypes = async (): Promise<ContactType[]> =>
    this.Get("/api/ContactType").then(r => (r?.status === 200 ? r.data : []));

  createContactType = async (dto: ContactTypeDto): Promise<ContactType | null> =>
    this.Post("/api/ContactType", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null));

  updateContactType = async (id: number, dto: ContactTypeDto): Promise<boolean> =>
    this.Put(`/api/ContactType/${id}`, dto).then(r => r?.status === 200 || r?.status === 204);

  deleteContactType = async (id: number): Promise<boolean> =>
    this.Delete(`/api/ContactType/${id}`).then(r => r?.status === 200 || r?.status === 204);

  getCrmEventTypes = async (): Promise<CrmEventType[]> =>
    this.Get("/api/CrmEventType").then(r => (r?.status === 200 ? r.data : []));

  createCrmEventType = async (dto: CrmEventTypeDto): Promise<CrmEventType | null> =>
    this.Post("/api/CrmEventType", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null));

  updateCrmEventType = async (id: number, dto: CrmEventTypeDto): Promise<boolean> =>
    this.Put(`/api/CrmEventType/${id}`, dto).then(r => r?.status === 200 || r?.status === 204);

  deleteCrmEventType = async (id: number): Promise<boolean> =>
    this.Delete(`/api/CrmEventType/${id}`).then(r => r?.status === 200 || r?.status === 204);

  getExternalCompanies = async (): Promise<ExternalCompany[]> =>
    this.Get("/api/ExternalCompany").then(r => (r?.status === 200 ? r.data : []));

  getExternalCompanyById = async (id: number): Promise<ExternalCompany | null> =>
    this.Get(`/api/ExternalCompany/${id}`).then(r => (r?.status === 200 ? r.data : null));

  createExternalCompany = async (dto: ExternalCompanyDto): Promise<ExternalCompany | null> =>
    this.Post("/api/ExternalCompany", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null));

  updateExternalCompany = async (id: number, dto: ExternalCompanyDto): Promise<boolean> =>
    this.Put(`/api/ExternalCompany/${id}`, dto).then(r => r?.status === 200 || r?.status === 204);

  deleteExternalCompany = async (id: number): Promise<boolean> =>
    this.Delete(`/api/ExternalCompany/${id}`).then(r => r?.status === 200 || r?.status === 204);

  // UPDATED: CRM Events with performance optimization
  getCrmEvents = async (includeDetails = false): Promise<CrmEvent[]> => {
    const url = includeDetails ? "/api/CrmEvent?includeDetails=true" : "/api/CrmEvent";
    return this.Get(url).then(r => (r?.status === 200 ? r.data : []));
  };

  getCrmEventById = async (id: number): Promise<CrmEvent | null> =>
    this.Get(`/api/CrmEvent/${id}`).then(r => (r?.status === 200 ? r.data : null));

  createCrmEvent = async (dto: CrmEventDto): Promise<CrmEvent | null> =>
    this.Post("/api/CrmEvent", dto).then(r => (r?.status === 200 || r?.status === 201 ? r.data : null));

  updateCrmEvent = async (id: number, dto: CrmEventDto): Promise<boolean> =>
    this.Put(`/api/CrmEvent/${id}`, dto).then(r => r?.status === 200 || r?.status === 204);

  deleteCrmEvent = async (id: number): Promise<boolean> =>
    this.Delete(`/api/CrmEvent/${id}`).then(r => r?.status === 200 || r?.status === 204);

  getCrmEventsByContact = async (contactId: number): Promise<CrmEvent[]> =>
    this.Get(`/api/CrmEvent/contact/${contactId}`).then(r => (r?.status === 200 ? r.data : []));

  getCrmEventsByExternalCompany = async (companyId: number): Promise<CrmEvent[]> =>
    this.Get(`/api/CrmEvent/company/${companyId}`).then(r => (r?.status === 200 ? r.data : []));
}